import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import MuiAvatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import type { SxProps, Theme } from '@mui/material/styles';
import { getSignatureColor } from 'components/editor/shared/components/SignatureBaseButton';
import Icon from 'components/icon';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { ContentType } from 'utils/getContentType';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useGetCrmContactByIdQuery } from 'hooks/useGetCrmContactByIdQuery';
import { useCrmContactsByCompanyId } from 'pages/flex-data/flex-data-links/useCrmContactsByCompanyId';
import { useSigneeOptionsQuery } from './useSigneeOptionsQuery';
import { toProspectCompositeId } from 'services/company-and-contacts/mappers';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';
import { useAccountUsersQuery } from 'hooks/useAccountUsersQuery';
import { buildSigneeOptions } from './buildSigneeOptions';
import type { SignatureSelectOption } from './types';
import { signeeReassignPickerTestIds } from './SigneeReassignPicker.test-ids';

type Props = {
  value: SignatureSelectOption | null | undefined;
  onChange: (newValue: SignatureSelectOption | null | undefined) => void;
  accountId: number;
  placeholder?: string;
};

const sectionHeaderSx: SxProps<Theme> = {
  px: 1,
  pt: 1,
  pb: 0.25,
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'text.secondary',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const optionBoxSx: SxProps<Theme> = {
  px: 1,
  py: 0.5,
  cursor: 'pointer',
  borderRadius: 1,
  '&:hover': { backgroundColor: 'action.hover' },
};

interface SigneeGroupProps {
  title: string;
  items: SignatureSelectOption[];
  selectedValue: string | undefined;
  onSelect: (option: SignatureSelectOption) => void;
}

function SigneeGroup({ title, items, selectedValue, onSelect }: SigneeGroupProps) {
  if (items.length === 0) return null;

  return (
    <>
      <Typography sx={sectionHeaderSx}>{title}</Typography>
      {items.map((option) => (
        <Box
          key={option.value}
          data-testid={signeeReassignPickerTestIds.option}
          sx={{ ...optionBoxSx, ...(option.value === selectedValue ? { backgroundColor: 'action.selected' } : {}) }}
          onClick={() => onSelect(option)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MuiAvatar
              className={`user_color ${getSignatureColor(option.email)}`}
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.75rem',
                fontWeight: 600,
                flexShrink: 0,
                backgroundColor: 'var(--user-bg-color)',
                color: 'var(--user-border-color)',
                border: '1px solid var(--user-border-color)',
              }}
            >
              {option.initials}
            </MuiAvatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }} noWrap>
                {option.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, wordBreak: 'break-all' }} noWrap>
                {option.email}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </>
  );
}

export function SigneeReassignPicker({ value, onChange, accountId, placeholder }: Props) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const contentId = useAppSelector(selectContentId);
  const { data: documentData } = useContentMetadata(ContentType.DOCUMENT, contentId);
  const prospectId = toProspectCompositeId(accountId, documentData?.prospect);
  const isCrmPersonProspect = !!prospectId && documentData?.prospect?.type === ProspectType.PERSON;
  const isCrmCompanyProspect = !!prospectId && !isCrmPersonProspect;

  const { contacts: crmContacts, isLoading: isCrmContactsLoading } = useCrmContactsByCompanyId({
    companyId: isCrmCompanyProspect ? prospectId : undefined,
    enabled: isCrmCompanyProspect,
  });
  const { data: crmContact, isFetching: isCrmContactFetching } = useGetCrmContactByIdQuery(isCrmPersonProspect ? prospectId : null, {
    enabled: isCrmPersonProspect,
  });
  const isCompanyFetching = isCrmPersonProspect ? isCrmContactFetching : isCrmContactsLoading;
  const { data: signeesData, isFetching: isSigneesFetching } = useSigneeOptionsQuery({ accountId, inputValue, enabled: true });
  const { data: usersData, isFetching: isUsersFetching } = useAccountUsersQuery();

  const isLoading = isCompanyFetching || isSigneesFetching || isUsersFetching;

  const resolvedCrmContacts = isCrmPersonProspect ? (crmContact ? [crmContact] : []) : crmContacts;

  const { contacts, users } = useMemo(
    () => buildSigneeOptions({ crmContacts: resolvedCrmContacts, signeesData, usersData, inputValue }),
    [resolvedCrmContacts, signeesData, usersData, inputValue]
  );

  const handleSelect = (option: SignatureSelectOption) => onChange(option);

  const isEmpty = !isLoading && contacts.length === 0 && users.length === 0;

  return (
    <Box>
      <TextField
        variant="standard"
        data-testid={signeeReassignPickerTestIds.searchInput}
        fullWidth
        autoFocus
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon name="IcoSearch" />
            </InputAdornment>
          ),
          endAdornment: isLoading ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <IconButton size="small" onClick={() => setInputValue('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        }}
      />
      <Box sx={{ mt: 1, maxHeight: 280, overflowY: 'auto' }}>
        {isEmpty && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
            {t('editor.signature.no_results')}
          </Typography>
        )}
        <SigneeGroup title={t('editor.signature.contacts')} items={contacts} selectedValue={value?.value} onSelect={handleSelect} />
        <SigneeGroup title={t('editor.signature.users')} items={users} selectedValue={value?.value} onSelect={handleSelect} />
      </Box>
    </Box>
  );
}
